import {
  AlertType,
  IJobInfo,
  ISystemUserInput,
  ISystemUserTabOrderInput,
  ISystemUserTabOrderResetMutationResult,
  ISystemUserTabOrderResetMutationVariables,
  ISystemUserTabOrderUpdateMutationResult,
  ISystemUserTabOrderUpdateMutationVariables,
  Maybe,
} from '../../graphql/schema/p4types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DetailsList, IDragDropContext, IDragDropEvents, Selection } from '@fluentui/react';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useMutation } from '@apollo/client';
import SystemUserTabOrderMutation from '../../graphql/mutations/system/SystemUserTabOrderMutation.graphql';
import SystemUserTabOrderResetMutation from '../../graphql/mutations/system/SystemUserTabOrderResetMutation.graphql';
import UserContext from '../Context/UserContext';
import normalizeVariables, { normalizeFields } from '../../graphql/schema/normalizeVariables';
import getClientMutationId from '../../utils/getClientMutationId';
import { BasicHeader } from '../../actions/BasicHeader';
import { ModalBottomButtonContainer } from '../modals/Styles';
import { useLocation, useRouteMatch } from 'react-router';
import { PrimaryButton, SecondaryButton, SuccessButton } from './Button';
import { useBackgroundProcess } from '../../utils/BackgroundJobs';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import FluentModal from '../modals/FluentModal';
import { StarAdd20Regular, StarCheckmark20Filled } from '@fluentui/react-icons';
import { Tooltip, Badge } from '@fluentui/react-components';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';

const tabNamesToItems = (tabNames: Array<string>, tabFavouriteMap?: Map<number, boolean>) => {
  return tabNames.map((tabName, index) => {
    return {
      key: `tab${index}`,
      name: tabName,
      tabFavourite: tabFavouriteMap?.get(index) ?? false,
    };
  });
};

const LIST_COLUMNS = [
  { key: 'name', name: 'Name', fieldName: 'name', minWidth: 100, maxWidth: 200, isResizable: true },
];

const NameCell = ({ item, onFavouriteToggle }: { item: any; onFavouriteToggle: (value: boolean) => void }) => {
  const [hover, setHover] = useState(false);

  const toggleTabFavourite = (e: React.MouseEvent<SVGElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onFavouriteToggle(!item?.tabFavourite);
  };

  return (
    <div
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div>
        {item.name}
        {item?.tabFavourite && (
          <Badge style={{ marginLeft: '16px' }} shape="rounded" color={'informative'}>
            Selected
          </Badge>
        )}
      </div>
      <Tooltip
        relationship={'label'}
        content={`${!item?.tabFavourite ? 'Set' : 'Remove'} "${item.name}" as the default tab for this form`}
        positioning="before"
        withArrow={true}
      >
        <span data-selection-disabled={true} style={{ paddingRight: '20px' }}>
          {hover || item?.tabFavourite ? (
            <StarCheckmark20Filled
              onClick={toggleTabFavourite}
              style={{ color: p4Tokens.colorFavouriteFilled, cursor: 'pointer' }}
            />
          ) : (
            <StarAdd20Regular onClick={toggleTabFavourite} style={{ cursor: 'pointer' }} />
          )}
        </span>
      </Tooltip>
    </div>
  );
};

export const TabOrganizationModal = ({
  tabNames,
  modalOpen,
  onClose,
  screenName,
  tabFavouriteMap,
}: {
  tabNames: Array<string>;
  modalOpen: boolean;
  onClose: () => void;
  screenName: Maybe<string>;
  tabFavouriteMap?: Map<number, boolean>;
}) => {
  const [listItems, setListItems] = useState(tabNamesToItems(tabNames, tabFavouriteMap));
  const [savingTabOrder, setSavingTabOrder] = useState<boolean>(false);

  useEffect(() => {
    setListItems(tabNamesToItems(tabNames, tabFavouriteMap));
  }, [tabNames]);

  const selectionRef = useRef<Selection>(new Selection());
  const draggedItemRef = useRef<any>();
  const draggedIndexRef = useRef(-1);
  const handleQueryError = useComponentQueryErrorHandling();

  const { dispatchSimpleToast } = useP4ToastController();

  const saveBackgroundProcess = useBackgroundProcess({
    title: 'Updating Tab Orders',
    onComplete: () => {
      setSavingTabOrder(false);
      //We could make an optimization here to update the cache directly of the users' tab orders query result - However considering the low expected use-frequency of the
      // feature & complexity involved in cache updates, we can just re-query for now.
      userContext.refetchTabOrders().then(() => {
        dispatchSimpleToast('Tab Update', 'Tab positions have been updated.', AlertType.Info);
      });
      onClose();
    },
    onFailed: () => {
      setSavingTabOrder(false);
    },
  });

  const [saveTabOrder] = useMutation<
    ISystemUserTabOrderUpdateMutationResult,
    ISystemUserTabOrderUpdateMutationVariables
  >(SystemUserTabOrderMutation, {
    onCompleted: (result) => {
      saveBackgroundProcess(result?.systemUserTabOrdersUpdate?.result as IJobInfo);
    },
    onError: (error) => {
      setSavingTabOrder(false);
      handleQueryError(error);
    },
  });

  const resetBackgroundProcess = useBackgroundProcess({
    title: 'Reset Tab Order',
    onComplete: () => {
      //We could make an optimization here to update the cache directly of the users' tab orders query result - However considering the low expected use-frequency of the
      // feature & complexity involved in cache updates, we can just re-query for now.
      userContext.refetchTabOrders().then(() => {
        dispatchSimpleToast('Tab Update', 'Tab positions have been reset.', AlertType.Info);
      });
      onClose();
    },
  });

  const [resetTabOrder] = useMutation<
    ISystemUserTabOrderResetMutationResult,
    ISystemUserTabOrderResetMutationVariables
  >(SystemUserTabOrderResetMutation, {
    onCompleted: (result) => {
      resetBackgroundProcess(result?.systemUserTabOrdersRemove?.result as IJobInfo);
    },
    onError: handleQueryError,
  });

  const userContext = useContext(UserContext);
  const location = useLocation();
  const match = useRouteMatch();

  const insertBeforeItem = (targetItem: any) => {
    const draggedItems = selectionRef.current.isIndexSelected(draggedIndexRef.current)
      ? selectionRef.current.getSelection()
      : [draggedItemRef.current!];

    const potentialInsertIndex = listItems.indexOf(targetItem);
    const insertIndex = potentialInsertIndex > 0 ? potentialInsertIndex : 1;
    if (insertIndex > potentialInsertIndex) {
      dispatchSimpleToast('Reserved Position', `${tabNames[0]} tab position is reserved`, AlertType.Warning);
    }
    const items = listItems.filter((itm) => draggedItems.indexOf(itm) === -1);

    items.splice(insertIndex, 0, ...draggedItems);

    setListItems(items);
  };

  const getDragDropEvents: () => IDragDropEvents = () => {
    return {
      canDrop: (dropContext?: IDragDropContext, dragContext?: IDragDropContext) => {
        return true;
      },
      canDrag: (item?: any) => {
        return item?.label !== 'Overview';
      },
      onDragEnter: (item?: any, event?: DragEvent) => {
        // return string is the css classes that will be added to the entering element.
        return '';
      },
      onDragLeave: (item?: any, event?: DragEvent) => {},
      onDrop: (item?: any, event?: DragEvent) => {
        if (draggedItemRef.current) {
          insertBeforeItem(item);
        }
      },
      onDragStart: (item?: any, itemIndex?: number, selectedItems?: any[], event?: MouseEvent) => {
        draggedItemRef.current = item;
        draggedIndexRef.current = itemIndex!;
      },
      onDragEnd: (item?: any, event?: DragEvent) => {
        draggedItemRef.current = undefined;
        draggedIndexRef.current = -1;
      },
    };
  };

  const onSaveTabOrderClick = () => {
    setSavingTabOrder(true);
    const systemUserTabOrdersList = listItems.map((value, index) => {
      const systemUserTabOrder: ISystemUserTabOrderInput = {
        systemUser: normalizeVariables(userContext.currentUser.systemUser, [
          ...normalizeFields,
          'msftAccessTokenSet',
        ]) as ISystemUserInput,
        viewScreenName: screenName || match.path || location.pathname,
        tabName: value.name,
        tabPosition: index,
        tabFavourite: value.tabFavourite,
      };
      return systemUserTabOrder;
    });

    saveTabOrder({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          systemUserTabOrderCriteria: { systemUserTabOrderList: systemUserTabOrdersList },
        },
      },
    }).then(() => {
      //Handled above
    });
  };

  const onResetTabOrderClick = () => {
    resetTabOrder({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          systemUserId: userContext.currentUser.systemUser.id,
          screenName: screenName || match.path || location.pathname,
        },
      },
    }).then(() => {
      //Handled above
    });
  };

  return (
    <FluentModal
      isOpen={modalOpen}
      styles={{
        main: { minWidth: '500px', overflowY: 'unset' },
        scrollableContent: { maxHeight: '97vh', paddingBottom: '60px' },
      }}
      layerProps={{ eventBubblingEnabled: false }}
    >
      <BasicHeader>Organize Tabs</BasicHeader>
      <p style={{ paddingLeft: '22px' }}>Select one or more rows, then click and drag to change the position.</p>
      {!savingTabOrder && (
        <DetailsList
          setKey="items"
          items={listItems}
          columns={LIST_COLUMNS}
          selection={selectionRef.current}
          selectionPreservedOnEmptyClick={true}
          dragDropEvents={getDragDropEvents()}
          ariaLabelForSelectionColumn="Toggle selection"
          ariaLabelForSelectAllCheckbox="Toggle selection for all items"
          checkButtonAriaLabel="select row"
          onRenderItemColumn={(item, _, column) => {
            if (column?.key === 'name') {
              return (
                <NameCell
                  item={item}
                  onFavouriteToggle={(value) => {
                    const itemsCopy = [...listItems];

                    itemsCopy.forEach((i) => (i.tabFavourite = i.name === item.name ? value : false));

                    setListItems(itemsCopy);
                  }}
                />
              );
            }
            return item[column?.fieldName as keyof typeof item];
          }}
        />
      )}
      <ModalBottomButtonContainer>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        <SuccessButton onClick={onResetTabOrderClick} disabled={savingTabOrder}>
          Reset Tab Order
        </SuccessButton>
        <PrimaryButton onClick={onSaveTabOrderClick} disabled={savingTabOrder}>
          Save Tab Order
        </PrimaryButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};
